import { groupBy, keyBy, mapValues, orderBy, partition } from "lodash-es";
import { capitalizeEachWord } from "../../utils/capitalize";
import type { MergeTagV2, Tags } from "../merge-tag/types";
import type { ToTarget } from "../to/to-target";
import type { TagGroup } from "./components/types";

export const sortTags = <T extends MergeTagV2>(
  tags: T[],
  activeSection?: "recipient" | "subject",
  val?: string,
): T[] => {
  return orderBy(tags, [
    (x) => x.category_key !== activeSection,
    (x) => x.category_key,
    (x) => val && (x.field_name.toLowerCase().startsWith(val.toLowerCase()) ? 0 : 1),
    (x) => x.field_name,
  ]);
};

const recipientDisplayName = ({
  toTarget,
  target_connection_type,
  isManagerConnection,
}: ToTarget): string => {
  if (toTarget === "employee") {
    return "Recipient (person in segment)";
  }
  if (isManagerConnection) {
    return `Recipient (${toTarget === "connection" ? capitalizeEachWord(target_connection_type) : "Manager"})`;
  }
  return "Recipient";
};

export const groupTags = (tags: MergeTagV2[], toTargetObj: ToTarget): TagGroup => {
  const [sharedTags, personSpecificTags] = partition(tags, (x) => x.category_key === null);
  const { toTarget, target_connection_type, isManagerConnection, notify_channel } = toTargetObj;

  let tagGroup: TagGroup = {};
  if (isManagerConnection) {
    tagGroup = {
      ...tagGroup,
      recipient: {
        title: recipientDisplayName(toTargetObj),
        groupTags: personSpecificTags
          .filter((x) => x.category_key && ["manager", "connection"].includes(x.category_key))
          .map((x) => ({
            ...x,
            category_key: "recipient",
            id: x.id.replace(
              `${x.category_key === "connection" ? target_connection_type : "manager"}.`,
              "recipient.",
            ),
          })),
      },
    };
  }

  if (toTarget === "employee") {
    tagGroup = {
      ...tagGroup,
      recipient: {
        title: recipientDisplayName(toTargetObj),
        subtitle: (subGroup) => {
          if (["recipient", "subject"].includes(subGroup)) return;

          const connectionIsRecipient =
            subGroup === toTarget || subGroup === target_connection_type;

          if (connectionIsRecipient) {
            return `Their ${capitalizeEachWord(subGroup)} (Recipient)`;
          }

          return `Their ${capitalizeEachWord(subGroup)}`;
        },
        groupTags: personSpecificTags.map((x) => {
          if (x.category_key !== "subject") return x;

          return {
            ...x,
            category_key: "recipient",
            category_name: "Recipient",
            id: x.id.replace("subject.", "recipient."),
          };
        }),
      },
    };
  }
  if (["notify", "manager", "connection"].includes(toTarget) || notify_channel) {
    tagGroup = {
      ...tagGroup,
      subject: {
        title: "Subject (person in segment)",
        subtitle: (subGroup) => {
          if (["recipient", "subject"].includes(subGroup)) return;

          const connectionIsRecipient =
            subGroup === toTarget || subGroup === target_connection_type;

          if (connectionIsRecipient) {
            return `Their ${capitalizeEachWord(subGroup)} (Recipient)`;
          }

          return `Their ${capitalizeEachWord(subGroup)}`;
        },
        groupTags: personSpecificTags,
      },
    };
  }

  const [customEvents, misc] = partition(sharedTags, (x) => x.field_key.startsWith("event_"));

  return {
    ...tagGroup,
    customEvents: {
      title: "Custom events",
      groupTags: sortTags(customEvents),
    },
    miscellaneous: {
      title: "Miscellaneous",
      groupTags: sortTags(misc),
    },
  };
};

export const validMergeTags = (tags: Tags, toTarget: ToTarget): Tags => {
  const [tagsV1, tagsV2] = partition(tags, (x) => x.version === 1);
  const grouped = groupTags(tagsV2, toTarget);

  return [
    ...tagsV1,
    ...Object.values(grouped)
      .map((x) => x.groupTags)
      .flat(),
  ];
};

type UnlayerMergeTag = {
  name: string;
  value: string;
  // We need to use the `previewSample` property name here, rather than directly using `sample`
  // otherwise Unlayer will use the sample data as the merge tag display name in the editor.
  previewSample: string;
};
export type UnlayerMergeTags = {
  [group: string]: {
    name: string;
    mergeTags: { [key: string]: UnlayerMergeTag };
  };
};

export const mapUnlayerMergeTags = (mergeTags: Tags, toTarget: ToTarget): UnlayerMergeTags => {
  const groupedTags = groupTags(
    mergeTags.filter((x) => x.version === 2),
    toTarget,
  );

  const recipientTags = sortTags(
    groupedTags.recipient?.groupTags.map((x) => ({
      ...x,
      category_display_name:
        x.category_key === "recipient" ? "Recipient" : `Recipient’s ${x.category_name}`,
      field_name:
        x.category_key === "recipient" ? x.field_name : `${x.category_name} ${x.field_name}`,
    })) ?? [],
    "recipient",
  );

  const subjectTags = sortTags(
    groupedTags.subject?.groupTags.map((x) => ({
      ...x,
      category_display_name:
        x.category_key === "subject" ? "Subject" : `Subject’s ${x.category_name}`,
      field_name:
        x.category_key === "subject" ? x.field_name : `${x.category_name} ${x.field_name}`,
    })) ?? [],
    "subject",
  );

  return mapValues(
    groupBy(
      [
        ...recipientTags,
        ...subjectTags,
        ...(groupedTags.customEvents?.groupTags.map((x) => ({
          ...x,
          category_display_name: "Custom Events",
        })) ?? []),
        ...(groupedTags.miscellaneous?.groupTags.map((x) => ({
          ...x,
          category_display_name: "Miscellaneous",
        })) ?? []),
      ],
      (x) => x.category_display_name,
    ),
    (tags, key) => ({
      name: capitalizeEachWord(key),
      mergeTags: mapValues(keyBy(tags, "id"), ({ field_name, id, sample }) => ({
        name: field_name,
        value: `{{${id}}}`,
        previewSample: sample,
      })),
    }),
  );
};
